// Promotional popup configuration

// Time in milliseconds before showing the popup after page load
// Default: 15000ms (15 seconds)
export const PROMO_POPUP_DELAY = 5000;

// Time in milliseconds to wait before re-showing popup after first dismissal
// Default: 60000ms (1 minute)
export const PROMO_RESHOW_DELAY = 50000;

// Maximum number of times to show the popup before permanently dismissing
// Default: 20 appearances
export const MAX_PROMO_APPEARANCES = 30;

// LocalStorage keys
const PROMO_DISMISSED_KEY = "earlyBirdPromo2026Dismissed";
const PROMO_FIRST_DISMISSED_TIME_KEY = "earlyBirdPromo2026FirstDismissedTime";
const PROMO_DISMISS_COUNT_KEY = "earlyBirdPromo2026DismissCount";
const PROMO_APPEARANCE_COUNT_KEY = "earlyBirdPromo2026AppearanceCount";

// Expiry date for the promotional offer (10% discount deadline)
export const PROMO_EXPIRY_DATE = new Date("2026-04-30T23:59:59");

// Check if the promo should be shown (before expiry date)
export const isPromoActive = (): boolean => {
  return new Date() < PROMO_EXPIRY_DATE;
};

// Get the number of times user has dismissed the popup
export const getDismissCount = (): number => {
  const count = localStorage.getItem(PROMO_DISMISS_COUNT_KEY);
  return count ? parseInt(count, 10) : 0;
};

// Get the number of times the popup has appeared
export const getAppearanceCount = (): number => {
  const count = localStorage.getItem(PROMO_APPEARANCE_COUNT_KEY);
  return count ? parseInt(count, 10) : 0;
};

// Increment the appearance count
export const incrementAppearanceCount = (): number => {
  const currentCount = getAppearanceCount();
  const newCount = currentCount + 1;
  localStorage.setItem(PROMO_APPEARANCE_COUNT_KEY, newCount.toString());

  // If we've reached max appearances, mark as permanently dismissed
  if (newCount >= MAX_PROMO_APPEARANCES) {
    localStorage.setItem(PROMO_DISMISSED_KEY, "true");
  }

  return newCount;
};

// Check if user has permanently dismissed the popup (reached max appearances or manually dismissed)
export const hasUserPermanentlyDismissedPromo = (): boolean => {
  return localStorage.getItem(PROMO_DISMISSED_KEY) === "true" ||
         getAppearanceCount() >= MAX_PROMO_APPEARANCES;
};

// Check if enough time has passed since first dismissal to re-show
export const shouldReshowAfterFirstDismissal = (): boolean => {
  const dismissCount = getDismissCount();

  // If never dismissed or already permanently dismissed, return false
  if (dismissCount === 0 || dismissCount >= 2) {
    return false;
  }

  // If dismissed once, check if enough time has passed
  if (dismissCount === 1) {
    const firstDismissedTime = localStorage.getItem(PROMO_FIRST_DISMISSED_TIME_KEY);
    if (firstDismissedTime) {
      const timeSinceFirstDismissal = Date.now() - parseInt(firstDismissedTime, 10);
      return timeSinceFirstDismissal >= PROMO_RESHOW_DELAY;
    }
  }

  return false;
};

// Mark the popup as dismissed (only used for first dismissal to start repeat cycle)
export const markPromoAsDismissed = (): void => {
  const dismissCount = getDismissCount();

  if (dismissCount === 0) {
    // First dismissal: save timestamp and increment count
    localStorage.setItem(PROMO_FIRST_DISMISSED_TIME_KEY, Date.now().toString());
    localStorage.setItem(PROMO_DISMISS_COUNT_KEY, "1");
  }
  // Note: Permanent dismissal now happens automatically after MAX_PROMO_APPEARANCES
  // via incrementAppearanceCount() function
};

// Reset promo state (useful for testing)
export const resetPromoState = (): void => {
  localStorage.removeItem(PROMO_DISMISSED_KEY);
  localStorage.removeItem(PROMO_FIRST_DISMISSED_TIME_KEY);
  localStorage.removeItem(PROMO_DISMISS_COUNT_KEY);
  localStorage.removeItem(PROMO_APPEARANCE_COUNT_KEY);
};
